import { action, computed, makeObservable, observable } from 'mobx'
import { debounceTime, fromEvent, merge, Subscription, tap } from 'rxjs'
import UserData from 'iam/models/UserData'
import { UserDataType } from 'iam/models/UserDataType'
import Consultation from 'jobs/consultations/Consultation'
import ConsultationStore from 'jobs/consultations/ConsultationStore'
import { Id } from 'types/definitions'
import { toStream } from 'utils/mobx-util'
import styles from './UpcomingConsultationPreview.sass'

export interface UpcomingConsultation {
  clientDisplayName: string
  consultationStartDate: string
  jobId: string
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

export default class UpcomingConsultationPreviewViewModel {
  /**
   * Subscription to refresh timer.
   */
  private subscription = new Subscription()

  /**
   * Whether the upcoming consultation preview is expanded.
   */
  @observable
  isExpanded = false

  /**
   * The maximum amount of upcoming calls to display in the preview.
   */
  @observable
  maxCallItems = 3

  /**
   * Array of at most the next three scheduled consultations.
   */
  @observable
  bookedConsultations: Consultation[] = []

  /**
   * Stream updating the max call items in the accordion after window resize events.
   */
  private resize$ = merge(
    toStream(() => this.isExpanded).pipe(
      tap((expanded) => {
        if (expanded) this.fetchConsultations()
      }),
    ),
    fromEvent(window, 'resize').pipe(
      debounceTime(50),
      tap(() => {
        this.updateMaxCallItems()
      }),
    ),
  )

  constructor(
    private consultationStore: ConsultationStore,
    private getUserData: () => UserData | null,
  ) {
    makeObservable(this)
  }

  /**
   * Activates the view model.
   */
  activate() {
    this.fetchConsultations()
    this.subscription = this.resize$.subscribe()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
    this.reset()
  }

  /**
   * Whether the user is a pro.
   */
  @computed
  get isPro() {
    return this.getUserData()?.userDataType === UserDataType.Pro
  }

  /**
   * Filtered array of at most the next three scheduled consultations.
   */
  @computed
  get filteredConsultations(): UpcomingConsultation[] {
    const max = this.maxCallItems > 0 ? this.maxCallItems : 1

    return (
      this.bookedConsultations
        // Filter out start times earlier than present time plus 15 minutes.
        // AKA don't show consultations that started 15 or more minutes ago.
        .filter(
          (c) =>
            !!c.bookedTime?.startTime &&
            c.bookedTime.startTime.valueOf() >= Date.now() + 900000,
        )
        .sort((a, b) => {
          return (
            a.bookedTime!.startTime.valueOf() -
            b.bookedTime!.startTime.valueOf()
          )
        })
        .slice(0, max)
        .map((c) => {
          return {
            clientDisplayName: c.clientDisplayName,
            consultationStartDate: dateFormatter.format(
              c.bookedTime!.startTime,
            ),
            jobId: Id.assert(c.jobId),
          }
        })
    )
  }

  /**
   * Whether there are any upcoming consultations.
   */
  @computed
  get areConsultationsUpcoming() {
    return this.filteredConsultations.length > 0
  }

  /**
   * Sets isExpanded.
   */
  @action.bound
  setIsExpanded(isExpanded: boolean) {
    this.isExpanded = isExpanded
  }

  /**
   * Toggles isExpanded.
   */
  @action.bound
  toggleIsExpanded() {
    this.isExpanded = !this.isExpanded
  }

  /**
   * Sets maxCallItems.
   */
  @action.bound
  setMaxCallItems(max: number) {
    this.maxCallItems = max
  }

  /**
   * Sets bookedConsultations.
   */
  @action.bound
  setBookedConsultations(consultations: Consultation[]) {
    this.bookedConsultations = consultations
  }

  /**
   * Resets isExpanded and maxCallItems to their default states and clears upcomingConsultations.
   */
  @action.bound
  reset() {
    this.setIsExpanded(false)
    this.setMaxCallItems(3)
    this.setBookedConsultations([])
  }

  /**
   * Updates max call items in row.
   */
  updateMaxCallItems() {
    const row = window.document.getElementsByClassName(styles.row)?.[0]
    if (!row) return

    const maxItems = Math.floor((row.clientWidth - 48) / 360)
    this.setMaxCallItems(maxItems)
  }

  /**
   * Fetches booked consultations.
   */
  async fetchConsultations() {
    const bookedConsultations =
      await this.consultationStore.fetchBookedConsultations()

    this.setBookedConsultations(bookedConsultations)
  }
}
